<script setup lang="ts">
    import { SHEET_NAVIGATION_KEY } from '~/composables/useSheetNavigation';
    import useSheetPanelHeight from '~/composables/useSheetPanelHeight';

    const props = withDefaults(
        defineProps<{
            /** True when the sheet drives panels, i.e. when a `root-panel` was given. */
            navigable: boolean;
            /**
             * Trims the room left under the content, for the drawer rendering. A drawer
             * sits on the bottom edge of the screen and already reserves the home
             * indicator through `env(safe-area-inset-bottom)`, so the floor below that
             * is only what a device without an inset needs; the centred desktop dialog
             * keeps the roomier default.
             *
             * Two whole class strings rather than an interpolated number: Tailwind reads
             * these files as text to decide what to generate, so a value built at
             * runtime would produce no CSS at all.
             */
            compactBottom?: boolean;
        }>(),
        { compactBottom: false }
    );

    const bottomPadding = computed(() => (props.compactBottom ? 'pb-[max(5px,env(safe-area-inset-bottom))]' : 'pb-[max(16px,env(safe-area-inset-bottom))]'));

    const nav = inject(SHEET_NAVIGATION_KEY, null);
    const viewport = ref<HTMLElement | null>(null);

    const current = computed(() => (props.navigable ? nav?.current.value ?? null : null));

    useSheetPanelHeight(viewport, current);
</script>

<template>
    <!-- Owns the scroll (reka-ui arbitrates the dismiss swipe against the nearest
         scrollable ancestor, so this must stay the one that scrolls) and, when
         navigable, clips the panels travelling in and out on the x axis. -->
    <div
        ref="viewport"
        class="min-h-0 overflow-y-auto"
        :class="navigable ? 'relative overflow-x-hidden' : [bottomPadding, 'pt-1']"
    >
        <slot />
    </div>
</template>
